<script setup lang="ts">
  import { ref, watch } from 'vue'
  import { Select, Input, Checkbox, SelectOption } from 'ant-design-vue'
  import { AnalysisConfigOption } from '../models/AnalysisConfigOption'

  const props = defineProps({
    modelValue: { type: null, default: undefined }, // value for Select mode
    defaultModelValue: { type: null, default: undefined },
    alternativeModelValue: { type: null, default: undefined }, // value for Input mode
    inactiveFieldDefaultValue: { type: null, default: undefined },
    options: { type: Array<AnalysisConfigOption>, default: () => [] },
    placeholder: { type: String, default: '' },
    allowClear: { type: Boolean, default: true },
    toggleLabel: { type: String, default: 'Use custom value' }
  })

  const emit = defineEmits(['update:modelValue', 'update:alternativeModelValue'])

  const computeToggleState = (): boolean => {
    const m = props.modelValue
    const a = props.alternativeModelValue
    const inactive = props.inactiveFieldDefaultValue
    if (m === inactive && a !== inactive) return true
    if (a === inactive && m !== inactive) return false
    return false
  }

  // Local reactive mirrors of incoming props. These are used for v-model binding in the template.
  // Changes to these locals are emitted back to the parent.
  const isToggleEnabled = ref<boolean>(computeToggleState())
  const localSelectValue = ref<any>(props.modelValue || props.inactiveFieldDefaultValue)
  const localInputValue = ref<any>(props.alternativeModelValue ?? props.inactiveFieldDefaultValue)

  // Keep locals in sync when props change
  watch(
    () => props.modelValue,
    (v) => (localSelectValue.value = v),
    { immediate: true }
  )
  watch(
    () => props.alternativeModelValue,
    (v) => (localInputValue.value = v),
    { immediate: true }
  )

  // Emit updates when locals change so parent can bind with v-model:*
  watch(localSelectValue, (v) => emit('update:modelValue', v))
  watch(localInputValue, (v) => emit('update:alternativeModelValue', v))

  // When toggle changes we emit update for the toggle prop as well (if parent bound it)
  const onToggleChange = (checked: boolean) => {
    isToggleEnabled.value = checked

    if (checked) {
      localSelectValue.value = props.inactiveFieldDefaultValue
      localInputValue.value = ''
    } else {
      localInputValue.value = props.inactiveFieldDefaultValue
      localSelectValue.value = props.defaultModelValue
    }
  }
</script>

<template>
  <div>
    <Checkbox :checked="isToggleEnabled" @change="(e: any) => onToggleChange(e.target.checked)">
      {{ toggleLabel }}
    </Checkbox>

    <div class="mt-2">
      <Input
        v-if="isToggleEnabled"
        :value="localInputValue"
        @update:value="(v: any) => (localInputValue = v)"
        :placeholder="placeholder"
        size="large"
        class="w-full"
        allowClear
      />

      <!-- Select mode -->
      <Select
        v-else
        :value="localSelectValue"
        @change="(v: any) => (localSelectValue = v)"
        :allow-clear="allowClear"
        size="large"
        placeholder="Select..."
        class="w-full"
        showSearch
      >
        <SelectOption v-for="option in options" :key="option.value" :value="option.value">
          <span v-html="option.label"></span>
        </SelectOption>
      </Select>
    </div>
  </div>
</template>

<style scoped></style>
